import * as HoverCard from "@radix-ui/react-hover-card";
import * as Tooltip from "@radix-ui/react-tooltip";
import classNames from "classnames/bind";
import React from "react";
import { Icon } from "../Icon/Icon";
import styles from "./SideNavigation.module.scss";
import type {
  SideNavigationTabNestedAreaProps,
  SideNavigationTabProps,
  SideNavigationTabsProps,
} from "./types";

const cx = classNames.bind(styles);

const SideNavigationTabNestedArea = ({
  isOpen,
  children,
}: SideNavigationTabNestedAreaProps) => {
  return (
    <div
      className={cx("nested-tab-container", {
        collapsed: !isOpen,
      })}
    >
      <div
        className={cx("nested-tab-content", {
          expanded: isOpen,
          collapsed: !isOpen,
        })}
      >
        {children}
      </div>
    </div>
  );
};

const SideNavigationTab = ({
  tabDetails,
  activeTab,
  onClickNestedTab,
  isNestedTabOpen,
  isNestedTabActive,
  isCollapsed,
}: SideNavigationTabProps) => {
  const handleTabClick = (evt: React.MouseEvent<HTMLDivElement>) => {
    if (tabDetails.nested) {
      onClickNestedTab?.(evt);
    }
  };

  const tabIcon = tabDetails.icon ? (
    <Icon variant={tabDetails.icon} />
  ) : (
    tabDetails.title.charAt(0)
  );

  const tab = (
    <div
      className={cx("tab-container", {
        "tab-container--active": isNestedTabActive,
        "tab-container--open": isNestedTabOpen,
        "tab-container--collapsed": isCollapsed,
      })}
      onClick={handleTabClick}
    >
      <div className={cx("tab")}>
        <div className={cx("tab-content")}>
          <div className={cx("tab-icon")}>
            {!isCollapsed
              ? tabIcon
              : (tabDetails.renderLink?.({
                  id: tabDetails.id,
                  title: tabDetails.title,
                  isActive: activeTab === tabDetails.id,
                  icon: tabIcon,
                  className: cx("link"),
                }) ?? tabIcon)}
          </div>
          {!isCollapsed && (
            <p
              className={cx("tab-title")}
              data-testid="tab-title"
            >
              {tabDetails.renderLink?.({
                id: tabDetails.id,
                title: tabDetails.title,
                isActive: activeTab === tabDetails.id,
                className: cx("link"),
              }) ?? tabDetails.title}
            </p>
          )}
          {tabDetails.nested && !isCollapsed && (
            <div
              className={cx("tab-collapse-trigger-container")}
              onClick={onClickNestedTab}
              role="listdropdown"
            >
              <Icon variant={isNestedTabOpen ? "caret-up" : "caret-down"} />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const nestedTabEntries = tabDetails.nested && (
    <ul
      className={cx("nested-tab-entries-container", {
        "in-popover": isCollapsed,
      })}
    >
      {tabDetails.nested.map((detail, index) => {
        return (
          <li
            key={index}
            className={cx("tab-container", {
              "tab-container--active": activeTab === detail.id,
            })}
          >
            <p className={cx("nested-tab-title")}>
              {detail.renderLink?.({
                id: detail.id,
                title: detail.title,
                isActive: activeTab === detail.id,
                className: cx("link"),
              }) ?? detail.title}
            </p>
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      {isCollapsed ? (
        tabDetails.nested ? (
          <HoverCard.Root
            openDelay={150}
            closeDelay={0}
          >
            <HoverCard.Trigger asChild>{tab}</HoverCard.Trigger>
            <HoverCard.Portal>
              <HoverCard.Content
                side="right"
                sticky="always"
                // REVIEW: there is a bug in the underlying Radix implementation
                // that causes the hover card to close prematurely when the mouse
                // moves from the trigger to the content when sideOffset is set
                // sideOffset={12}
                align="start"
                collisionPadding={16}
                className={cx(
                  "popover-content",
                  tabDetails.nested && "popover-content--nested"
                )}
              >
                <p
                  className={cx(
                    "nested-tab-group-title",
                    tabDetails.nested && "nested-tab-group-title--nested"
                  )}
                >
                  {tabDetails.title}
                </p>
                {nestedTabEntries}
              </HoverCard.Content>
            </HoverCard.Portal>
          </HoverCard.Root>
        ) : (
          <Tooltip.Provider>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>{tab}</Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  className={cx("tooltip-content")}
                  sideOffset={12}
                  side="right"
                >
                  {tabDetails.title}
                  <Tooltip.Arrow className={cx("tooltip-arrow")} />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
        )
      ) : (
        tab
      )}

      {tabDetails.nested && !isCollapsed && (
        <SideNavigationTabNestedArea isOpen={isNestedTabOpen}>
          {nestedTabEntries}
        </SideNavigationTabNestedArea>
      )}
    </>
  );
};

const SideNavigationTabs = ({
  tabDetails,
  activeTab,
  openNestedTab,
  setOpenNestedTab,
  isCollapsed,
}: SideNavigationTabsProps) => {
  const isNestedTabOpen =
    openNestedTab === tabDetails.id ||
    !!tabDetails.nested?.find((detail) => detail.id === openNestedTab);
  const isNestedTabActive =
    activeTab === tabDetails.id ||
    !!tabDetails.nested?.find((tab) => tab.id === activeTab);

  const handleNestedTabClick = () => {
    // Collapse the dropdown when the user click on the dropdown twice
    if (openNestedTab === tabDetails.id) {
      setOpenNestedTab(null);
    } else {
      setOpenNestedTab(tabDetails.id);
    }
  };

  return (
    <SideNavigationTab
      tabDetails={tabDetails}
      activeTab={activeTab}
      onClickNestedTab={handleNestedTabClick}
      isNestedTabOpen={isNestedTabOpen}
      isNestedTabActive={isNestedTabActive}
      isCollapsed={isCollapsed}
    />
  );
};

export default SideNavigationTabs;
